import { useCallback } from 'react';
import moment from 'moment-timezone';
import { TIMEZONE } from '../../lib/constants/bookingForm';
import {
    validateDateNotInPast,
    validateReturnDateAfterDeparture,
    validateTimeNotInPast,
    validateReturnTimeAfterDeparture,
    validateRequired,
    validatePassengers,
} from '../helpers/bookingFormValidation';

/**
 * Custom hook for booking form validation
 */
export const useBookingValidation = (formData, todayDate, errors, setErrors, setTimeError) => {
    const validateForm = useCallback(() => {
        const newErrors = {};
        let hasTimeError = false;

        // Required field validation
        const requiredFields = [
            'pickUpAddress',
            'dropOffAddress', 
            'tripType',
            'noOfPassengers',
            'departureDate',
            'departureTime'
        ];

        requiredFields.forEach(field => {
            const error = validateRequired(formData[field], field);
            if (error) {
                newErrors[field] = error;
            }
        });

        // Validate passengers specifically
        if (formData.noOfPassengers) {
            const passengerError = validatePassengers(formData.noOfPassengers);
            if (passengerError) {
                newErrors.noOfPassengers = passengerError;
            }
        }

        // Validate departure date
        if (formData.departureDate) {
            const departureDateError = validateDateNotInPast(formData.departureDate, todayDate);
            if (departureDateError) {
                newErrors.departureDate = departureDateError;
            }
        }

        // Validate departure time
        if (formData.departureTime) {
            const departureTimeError = validateTimeNotInPast(formData.departureTime, formData.departureDate, todayDate);
            if (departureTimeError) {
                newErrors.departureTime = departureTimeError;
            }
        }

        // Return date validation for round trip
        if (formData.tripType === '2') {
            if (!formData.returnDate) {
                newErrors.returnDate = 'Please select return date.';
            } else {
                const returnDateError = validateDateNotInPast(formData.returnDate, todayDate);
                if (returnDateError) {
                    newErrors.returnDate = returnDateError;
                } else {
                    // Additional validation for return date vs departure date
                    const returnDateComparisonError = validateReturnDateAfterDeparture(formData.returnDate, formData.departureDate);
                    if (returnDateComparisonError) {
                        newErrors.returnDate = returnDateComparisonError;
                    }
                }
            }

            if (!formData.returnTime) {
                newErrors.returnTime = 'Please select return time.';
            } else {
                const returnTimeError = validateTimeNotInPast(formData.returnTime, formData.returnDate, todayDate);
                if (returnTimeError) {
                    newErrors.returnTime = returnTimeError;
                }
            }

            // Date comparison validation - only if both dates and times are present
            if (formData.departureDate && formData.departureTime && formData.returnDate && formData.returnTime) {
                const isReturnTimeValid = validateReturnTimeAfterDeparture(
                    formData.departureTime,
                    formData.returnTime,
                    formData.departureDate,
                    formData.returnDate
                );

                if (!isReturnTimeValid) {
                    hasTimeError = true;
                    setTimeError(true);
                } else {
                    hasTimeError = false;
                    setTimeError(false);
                }
            }
        } else {
            // Clear time error for one-way trips
            hasTimeError = false;
            setTimeError(false);
        }

        // Check for paste and manipulation errors
        const pasteAndManipulationErrors = [
            'pickUpAddress_pasteError',
            'pickUpAddress_manipulationError',
            'dropOffAddress_pasteError',
            'dropOffAddress_manipulationError'
        ];

        const hasPasteOrManipulationError = pasteAndManipulationErrors.some(errorKey => {
            const errorValue = errors[errorKey];
            const hasError = errorValue && errorValue.trim() !== '';
            if (hasError) {
                // console.log(`Found ${errorKey}:`, errorValue);
            }
            return hasError;
        });

        // Add paste and manipulation errors to newErrors if they exist
        pasteAndManipulationErrors.forEach(errorKey => {
            const errorValue = errors[errorKey];
            if (errorValue && errorValue.trim() !== '') {
                newErrors[errorKey] = errorValue;
            }
        });

        setErrors(newErrors);
        
        const isValid = Object.keys(newErrors).length === 0 && !hasTimeError && !hasPasteOrManipulationError;
        
        // console.log('Validation result:', {
        //     newErrorsCount: Object.keys(newErrors).length,
        //     hasTimeError,
        //     hasPasteOrManipulationError,
        //     isValid,
        //     allErrors: errors,
        //     newErrors
        // });
        
        return isValid;
    }, [formData, todayDate, errors, setErrors, setTimeError]);

    return {
        validateForm,
    };
};
